import { useEffect, useState } from 'react';
import {
  AccountData,
  AccountHistory,
  fetchAccountHistory,
  getAccountHistorySubject
} from '../../../../../store/Accounts';
import { getUsersByAccount } from '../../../../../store/PortalUser';
import { Team, getTeamsByAccount } from '../../../../../store/Team';
import { HiddenRecord, User } from '../../../../../types';
import { AuditTrail } from './AuditTrail';
import { AccountCustomizations } from './Customizations';
import { SubAccounts } from './SubAccounts';
import { Teams } from './Teams';
import { Users } from './Users';
import {
  AccountCustomizationData,
  defaultAccountCustomizationData,
  getAccountCustomizationsSubject
} from '../../../../../store/AccountCustomization';
import { PermissibleRender } from '../../../../Compounds/PermissibleRender';
import { Permission } from '../../../../../store/GeneralStore';
import { useInventory, getHiddenAssetsSubject } from '../../../../../store/Inventory';
import HiddenAssetsTable from './HiddenAssetsTable';

export interface AccountDetailsParams {
  data: AccountData;
}

export const AccountDetails = ({ data }: AccountDetailsParams) => {
  const teams: Team[] = getTeamsByAccount(data.id);
  const users: User[] = getUsersByAccount(data.id);
  const [acctCust, setAcctCust] = useState<AccountCustomizationData>(data?.accountCustomizations);
  const [auditTrail, setAuditTrail] = useState<AccountHistory[]>([]);
  const [hiddenAssets, setHiddenAssets] = useState<HiddenRecord[]>([]);
  const { fetchHiddenAssets } = useInventory();

  useEffect(() => {
    const auditSub = getAccountHistorySubject().subscribe((h) => setAuditTrail(h));
    const accountCustSub = getAccountCustomizationsSubject().subscribe((acc) => {
      setAcctCust(acc.find((a) => a.account_id === data.id) || defaultAccountCustomizationData(data.id, data.name));
    });
    const hiddenAssetsSub = getHiddenAssetsSubject().subscribe((assets) => setHiddenAssets(assets));

    return () => {
      if (auditSub) auditSub.unsubscribe();
      if (accountCustSub) accountCustSub.unsubscribe();
      if (hiddenAssetsSub) hiddenAssetsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchAccountHistory(data.id);
    setAcctCust(data?.accountCustomizations);
    fetchHiddenAssets(data.id, true);
  }, [data.id]);

  return (
    <div
      className="w-11/12 m-auto"
      data-testid="account-details"
      data-cy="account-details">
      <SubAccounts
        accountCustomizations={acctCust}
        account={data}
      />
      <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
        <AccountCustomizations data={acctCust} />
      </PermissibleRender>
      <PermissibleRender requiredPermissions={[Permission.CREATE_TEAM_USERS]}>
        <AccountCustomizations data={acctCust} />
      </PermissibleRender>
      <Users users={users} />
      <Teams teams={teams} />
      <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
        <AuditTrail data={auditTrail} />
      </PermissibleRender>
      <PermissibleRender requiredPermissions={[Permission.CREATE_TEAM_USERS]}>
        <AuditTrail data={auditTrail} />
      </PermissibleRender>
      <PermissibleRender requiredPermissions={[Permission.ADMIN_ACCOUNTS_TAB]}>
        <HiddenAssetsTable
          accountId={data.id}
          hiddenAssets={hiddenAssets}
        />
      </PermissibleRender>
    </div>
  );
};
